import React, { useState, useEffect } from 'react';
import { Flower2, Loader2, X, Check, Activity, Save, Wind, Sparkles, ChevronRight, Calculator, CalendarClock, Camera, Leaf, Eye, Volume2, Hand, Clock, Info, Calendar } from 'lucide-react';
import { FeatureContext, YogaPose, UserProfile } from '../types';
import { generateYogaRoutine } from '../services/geminiService';
import { getCachedPoseImage } from '../services/searchService';
import { saveYogaPlan, getCurrentUser } from '../services/backendService';
import { PoseImage } from './PoseImage';
import { NatureIntegrationDetail } from './NatureIntegrationDetail';
import logger from "@/utils/logger";
import { NatureIntegrationDetail as NatureDetailType } from '../types';

const isDallETemporaryUrl = (url: string | null | undefined): boolean => {
  if (!url) return false;
  return url.includes('oaidalleapiprodscus') || 
         url.includes('openai.com') ||
         url.includes('blob.core.windows.net');
};

interface YogaAidProps {
  activeContext?: FeatureContext | null;
  userProfile: UserProfile | null;
  onBack?: () => void;
  consultationResponse?: string | null;
  cache?: React.MutableRefObject<Map<string, any>>;
}

const YogaAid: React.FC<YogaAidProps> = ({ activeContext, userProfile, onBack, consultationResponse, cache }) => {
  const [routine, setRoutine] = useState<YogaPose[]>([]);
  const [classification, setClassification] = useState<'Acute' | 'Chronic' | null>(null);
  const [duration, setDuration] = useState<string | null>(null);
  const [phaseEmphasis, setPhaseEmphasis] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isGeneratingRationale, setIsGeneratingRationale] = useState(false);
  const [title, setTitle] = useState("Yoga Aid Plan");
  const [selectedPose, setSelectedPose] = useState<YogaPose | null>(null);
  const [natureIntegrationDetail, setNatureIntegrationDetail] = useState<NatureDetailType | null>(null);
  const [showNatureModal, setShowNatureModal] = useState(false);
  const [activeDoshaTab, setActiveDoshaTab] = useState<'vata' | 'pitta' | 'kapha'>('vata');

  const restoreFromCache = async (query: string): Promise<any> => {
    const cachedData = cache!.current.get(query)!;
    
    // Check each pose's image URL validity
    let needsUpdate = false;
    const validatedPoses = await Promise.all(
      cachedData.poses.map(async (pose: YogaPose) => {
        // If no image or if URL is temporary DALL-E link
        if (!pose.image?.url || isDallETemporaryUrl(pose.image.url)) {
          console.log(`[YogaAid Cache] Image missing or expired for "${pose.pose_name}" — re-resolving...`);
          needsUpdate = true;
          const freshImage = await getCachedPoseImage(pose.pose_name, pose.instructions, pose.benefit);
          return { ...pose, image: freshImage };
        }
        return pose;
      })
    );

    const updatedData = {
      ...cachedData,
      poses: validatedPoses
    };

    if (needsUpdate && cache) {
      cache.current.set(query, updatedData);
    }

    return updatedData;
  };

  const loadRoutine = async (query: string, displayTitle: string) => {
    // Check cache first
    if (cache?.current.has(query)) {
      console.log(`[YogaAid] Cache HIT for ailment: ${query} — restoring with image validation`);
      
      const cachedData = cache.current.get(query)!;
      const hasTemporaryUrls = cachedData.poses.some((p: YogaPose) => isDallETemporaryUrl(p.image?.url));
      
      if (hasTemporaryUrls) {
        setLoading(true); // Only show spinner if we need to re-resolve
      }

      try {
        const restoredData = await restoreFromCache(query);
        setRoutine(restoredData.poses);
        setClassification(restoredData.classification);
        setDuration(restoredData.duration);
        setPhaseEmphasis(restoredData.phaseEmphasis || null);
        setLoading(false);
        setTitle(displayTitle);
      } catch (err) {
        console.error("[YogaAid] Error during cache restoration:", err);
        // Fallback: trigger fresh generation
        cache.current.delete(query);
      }
      return;
    }

    console.log(`[YogaAid] Cache MISS for ailment: ${query} — loading curated protocol instantly and generating rationale in background`);
    
    setSaveSuccess(false);
    setTitle(displayTitle);
    setClassification(null);
    setDuration(null);
    setPhaseEmphasis(null);
    setNatureIntegrationDetail(null);
    setIsGeneratingRationale(true);

    // ── Step A: Generate local curated protocol instantly ──
    try {
      const userDosha = userProfile?.vikriti_dosha ?? userProfile?.prakriti_dosha ?? 'sama';
      const timeOfDay = new Date().getHours() < 12 ? 'morning' : (new Date().getHours() < 17 ? 'afternoon' : 'evening');
      
      const { getYogaProtocol, curatedPoseToYogaPoseShell, extractYogaSignalsFromConsultation } = await import('../services/yogaPoseLibrary');
      const { findLocalFuzzyMatch } = await import('../services/searchService');

      const consultationSignals = consultationResponse
        ? extractYogaSignalsFromConsultation(consultationResponse)
        : undefined;

      const curated = getYogaProtocol(query, userDosha, timeOfDay, consultationSignals);
      
      const initialPoses = [
        { ...curatedPoseToYogaPoseShell(curated.foundation, 'Foundation'), benefit: 'Curating personalized benefits...', contraindications: 'Curating precautions...', doshaNote: 'Analyzing...' },
        { ...curatedPoseToYogaPoseShell(curated.activation, 'Activation'), benefit: 'Curating activation benefit...', contraindications: 'Curating precautions...', doshaNote: 'Analyzing...' },
        { ...curatedPoseToYogaPoseShell(curated.pranayama, 'Internal Balance'), benefit: 'Curating internal balance benefit...', contraindications: 'Curating precautions...', doshaNote: 'Analyzing...' },
        { ...curatedPoseToYogaPoseShell(curated.nature, 'Nature Integration'), benefit: 'Curating nature benefit...', contraindications: 'Curating precautions...', doshaNote: 'Analyzing...' }
      ] as any[];

      const initialPosesWithImages = initialPoses.map(p => {
        const localUrl = findLocalFuzzyMatch(p.pose_name);
        return {
          ...p,
          image: localUrl ? { url: localUrl, source: 'local', confidence: 100 } : null
        };
      });

      setRoutine(initialPosesWithImages);
      // Turn off full-page spinner so curated content is visible immediately
      setLoading(false);
    } catch (e) {
      console.error("[YogaAid] Failed to load curated poses instantly, falling back to full loader:", e);
      setLoading(true);
      setRoutine([]);
    }

    // ── Step B: Fetch AI rationale in background ──
    try {
      const result = await generateYogaRoutine(query, userProfile, consultationResponse);
      if (result && result.poses.length > 0) {
        setClassification(result.classification);
        setDuration(result.duration);
        setPhaseEmphasis(result.phaseEmphasis || null);
        setNatureIntegrationDetail(result.natureIntegrationDetail || null);

        // Fetch visual details programmatically (Google CSE or DALL-E if missing)
        const { findLocalFuzzyMatch } = await import('../services/searchService');
        setImageLoading(true);
        const posesWithImages = await Promise.all(
          result.poses.map(async (p) => {
            const localUrl = findLocalFuzzyMatch(p.pose_name);
            if (localUrl) {
              return {
                ...p,
                image: { url: localUrl, source: 'local', confidence: 100 },
                image_url: p.image_url
              };
            }
            return {
              ...p,
              image: await getCachedPoseImage(p.pose_name, `${p.instructions}. ${p.benefit}`, p.vector_description),
              image_url: p.image_url
            };
          })
        );
        
        // Save to cache ONLY after all images are resolved
        if (cache) {
          const finalResult = {
            ...result,
            poses: posesWithImages
          };
          cache.current.set(query, finalResult);
          console.log(`[YogaAid Cache] Writing ${posesWithImages.length} poses | images: ${
            posesWithImages.filter(p => p.image?.url).length
          } resolved | sources: ${
            [...new Set(posesWithImages.map(p => p.image?.source ?? 'none'))].join(', ')
          }`);
        }

        setRoutine(posesWithImages);
        
        // Update selectedPose reference if it's currently open
        setSelectedPose(prev => {
          if (!prev) return null;
          const updated = posesWithImages.find(p => p.pose_name === prev.pose_name);
          return updated || prev;
        });

        setImageLoading(false);
      }
    } catch (e) {
      logger.error("Yoga Aid Background Generation Error:", e);
    } finally {
      setIsGeneratingRationale(false);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const user = getCurrentUser();
    if (!user) {
      alert("Please sign in to save routines.");
      return;
    }
    if (routine.length === 0) return;

    setSaveLoading(true);
    try {
      // Save full context including classification
      const result = await saveYogaPlan(user, {
        poses: routine,
        classification: classification || 'Acute',
        duration: duration || '7 days'
      }, title);

      if (result) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 5000);
      } else {
        alert("Failed to save routine. Please check your network connection.");
      }
    } catch (e) {
      logger.error("Save Error:", e);
      alert("An unexpected error occurred while saving.");
    } finally {
      setSaveLoading(false);
    }
  };

  useEffect(() => {
    if (activeContext) {
      const context = activeContext as any;
      if (context.cachedPoses && context.cachedPoses.length > 0) {
        setRoutine(context.cachedPoses);
        setClassification(context.classification || null);
        setDuration(context.duration || null);
        setPhaseEmphasis(context.phaseEmphasis || null);
        setTitle(context.id || "Saved Routine");
        setLoading(false);
        setSaveSuccess(true);
      } else if (activeContext.id) {
        // Use ID (Ailment Name) as the Title for consistency in Library, ignoring the generic card title
        loadRoutine(activeContext.id, activeContext.id);
      }
    } else {
      setRoutine([]);
      setTitle("Yoga Aid Plan");
      setSaveSuccess(false);
    }
  }, [activeContext]);

  return (
    <div className="h-full flex flex-col bg-white overflow-y-auto">
      <div className="bg-[#FDF0F5] border-b-2 border-[#C2185B] p-6 flex flex-col md:flex-row justify-between gap-4 items-center sticky top-0 z-20">
        <div className="flex items-center gap-4">
          {onBack && (
            <button onClick={onBack} className="flex items-center gap-2 p-2 rounded-xl text-[#AD1457] hover:text-[#C2185B] hover:bg-[#FDF0F5] transition-colors">
              <ChevronRight className="rotate-180" size={20} />
              <span className="text-sm font-bold uppercase tracking-widest">Back</span>
            </button>
          )}
          <div className="w-12 h-12 bg-[#C2185B] rounded-2xl flex items-center justify-center text-white shadow-sm">
            <Flower2 size={24} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-serif text-2xl font-bold text-[#C2185B]">Yoga Aid</h1>
              {cache?.current.has(activeContext?.id || '') && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase rounded-full border border-emerald-100">
                  <Check size={10} /> Restored from session
                </span>
              )}
            </div>
            <p className="text-[#AD1457] text-xs font-medium uppercase tracking-[0.2em] mt-0.5">Healing Through Movement</p>
          </div>
        </div>
      </div>

      <div className="p-8 max-w-6xl mx-auto w-full">
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-serif text-3xl font-bold text-sage-900 capitalize">{title}</h2>
          {routine.length > 0 && !loading && (
            <button
              onClick={handleSave}
              disabled={saveLoading || saveSuccess || isGeneratingRationale}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all ${saveSuccess
                ? 'bg-green-50 text-green-700 border border-green-200'
                : isGeneratingRationale
                  ? 'bg-sage-100 text-sage-400 cursor-not-allowed border border-sage-200'
                  : 'bg-sage-600 text-white hover:bg-sage-700 shadow-lg shadow-sage-100'
                }`}
            >
              {saveLoading || isGeneratingRationale ? <Loader2 size={16} className="animate-spin" /> : saveSuccess ? <Check size={16} /> : <Save size={16} />}
              {saveSuccess ? "Stored in Library" : isGeneratingRationale ? "Curating Protocol..." : "Save to Library"}
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="animate-spin text-sage-600 w-12 h-12 mb-4" />
            <p className="text-sage-700 font-bold">Nature Nani is curating your restorative protocol...</p>
          </div>
        ) : routine.length === 0 ? (
          <div className="text-center py-20 opacity-40">
            <Flower2 size={64} className="mx-auto mb-4" />
            <p>Start a consultation to generate a restorative plan.</p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Adaptive Guidance Badge */}
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex flex-col md:flex-row items-start gap-5 shadow-sm">
              <div className="bg-amber-100 p-3 rounded-2xl text-amber-700 shadow-inner">
                <CalendarClock size={28} />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <h3 className="font-bold text-amber-900 text-sm uppercase tracking-widest flex items-center gap-2">
                    <Activity size={14} /> Adaptive Guidance
                  </h3>
                  <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${classification === 'Acute' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                    {classification || 'General'}
                  </span>
                  {phaseEmphasis?.dominantDosha && (
                    <span className="px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-black uppercase tracking-tighter">
                      Personalized for {phaseEmphasis.dominantDosha} Vikriti
                    </span>
                  )}
                </div>
                <p className="text-amber-800 text-base font-medium mb-2">
                  {classification === 'Acute'
                    ? "Acute symptoms detected. Follow this protocol for at least 7 days."
                    : classification === 'Chronic'
                      ? `Chronic ${phaseEmphasis?.pacingDescriptor ? `(${phaseEmphasis.pacingDescriptor})` : ''} protocol. Follow for at least 21 days for nervous system recalibration.`
                      : "Standard protocol to restore natural rhythms."}
                </p>
                {phaseEmphasis?.sequencingRationale && (
                  <div className="mt-2 text-left">
                    <p className="text-amber-900 text-sm leading-relaxed">
                      <span className="font-semibold mr-1">Rationale:</span> 
                      <span className="font-normal italic">{phaseEmphasis.sequencingRationale}</span>
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Background Adaptation Banner */}
            {isGeneratingRationale && (
              <div className="bg-[#FAF8F5] border border-sage-200/60 rounded-2xl p-4 flex items-center justify-between shadow-sm animate-pulse">
                <div className="flex items-center gap-3 text-sage-800">
                  <Loader2 className="animate-spin text-sage-600 w-5 h-5 shrink-0" />
                  <span className="text-sm font-medium">Nature Nani is finalizing your personalized adaptations and clinical rationales in the background...</span>
                </div>
              </div>
            )}

            {/* Protocol Table */}
            <div className="bg-white border border-sage-100 rounded-2xl overflow-hidden shadow-sm">
              <div className="hidden md:grid bg-sage-50 px-6 py-4 border-b border-sage-100 grid-cols-12 gap-4 font-bold text-sage-700 text-xs uppercase tracking-widest">
                <div className="col-span-3">Phase</div>
                <div className="col-span-4">Action</div>
                <div className="col-span-5">Purpose</div>
              </div>
              <div className="divide-y divide-sage-50">
                {routine.map((pose, idx) => {
                  const weight = phaseEmphasis ? (
                    (pose.phase?.toLowerCase().includes('foundation') && phaseEmphasis.foundationWeight) ||
                    (pose.phase?.toLowerCase().includes('activation') && phaseEmphasis.activationWeight) ||
                    (pose.phase?.toLowerCase().includes('internal') && phaseEmphasis.internalBalanceWeight)
                  ) : null;

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                      setSelectedPose(pose);
                      const userDosha = userProfile?.vikriti_dosha || userProfile?.prakriti_dosha || 'vata';
                      const base = userDosha.split('-')[0];
                      setActiveDoshaTab(['vata', 'pitta', 'kapha'].includes(base) ? base as any : 'vata');
                    }}
                      className="flex flex-col md:grid md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 hover:bg-sage-50/50 cursor-pointer transition-colors group border-l-4 border-transparent hover:border-sage-300"
                    >
                      <div className="md:col-span-3">
                        <div className="flex flex-col items-start gap-1.5">
                          <span className="px-2.5 py-1 rounded-lg bg-sage-100 text-sage-800 font-bold text-[10px] uppercase tracking-widest leading-none">
                            {pose.phase || `Step ${idx + 1}`}
                          </span>
                          {weight && (
                            <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase border tracking-tight ${
                              weight === 'extended' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                              weight === 'reduced' ? 'bg-slate-50 text-slate-500 border-slate-100' :
                              'bg-amber-50 text-amber-700 border-amber-100'
                            }`}>
                              {weight} timing
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="md:col-span-4 flex flex-col gap-1">
                        <div className="font-bold text-sage-900 text-base md:text-lg flex items-center gap-2">
                          {pose.type === 'Pranayama' && <Wind size={18} className="text-blue-500" />}
                          {pose.type === 'Exercise' && <Activity size={18} className="text-orange-500" />}
                          {pose.type === 'Asana' && <Flower2 size={18} className="text-sage-600" />}
                          <span className="group-hover:text-sage-700 transition-colors uppercase tracking-tight">{pose.pose_name}</span>
                        </div>
                        {pose.doshaNote && (
                          <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest mt-0.5">
                            ✨ {pose.doshaNote}
                          </span>
                        )}
                      </div>
                      <div className="md:col-span-5 text-gray-700 text-sm leading-relaxed">
                        {pose.benefit}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Visual Grid (Secondary) */}
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                {routine.map((pose, idx) => {
                  const isNaturePhase = pose.phase?.toLowerCase().includes('nature');
                  const weight = phaseEmphasis ? (
                    (pose.phase?.toLowerCase().includes('foundation') && phaseEmphasis.foundationWeight) ||
                    (pose.phase?.toLowerCase().includes('activation') && phaseEmphasis.activationWeight) ||
                    (pose.phase?.toLowerCase().includes('internal') && phaseEmphasis.internalBalanceWeight)
                  ) : null;

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        if (isNaturePhase && natureIntegrationDetail) {
                          setShowNatureModal(true);
                        } else {
                          setSelectedPose(pose);
                          const userDosha = userProfile?.vikriti_dosha || userProfile?.prakriti_dosha || 'vata';
                          const base = userDosha.split('-')[0];
                          setActiveDoshaTab(['vata', 'pitta', 'kapha'].includes(base) ? base as any : 'vata');
                        }
                      }}
                      className={`relative group bg-white rounded-2xl overflow-hidden border border-sage-100 hover:shadow-xl transition-all cursor-pointer flex flex-col h-full ${
                        isNaturePhase 
                          ? 'md:col-span-12 min-h-[320px]' 
                          : 'md:col-span-6 lg:col-span-4'
                      }`}
                    >
                      <div className={`relative overflow-hidden flex items-center justify-center ${isNaturePhase ? 'h-64' : 'h-40'} bg-gray-200`}>
                        {pose.image ? (
                          <PoseImage poseName={pose.pose_name} imageResult={pose.image} />
                        ) : pose.image_url ? (
                          <img src={pose.image_url} alt={pose.pose_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full text-sage-200 bg-sage-50">
                            <Camera size={48} />
                          </div>
                        )}
                        <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                           <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest leading-none shadow-sm ${isNaturePhase ? 'bg-[#D69E2E] text-white' : 'bg-sage-100 text-sage-800'}`}>
                            {isNaturePhase ? 'Nature Rx' : pose.phase || `Step ${idx + 1}`}
                          </span>
                        </div>
                      </div>
                      <div className="p-4 flex-1 flex flex-col">
                        <div className="flex justify-between items-start mb-1">
                          <h3 className={`font-bold text-sage-900 group-hover:text-[#2D6A4F] transition-colors truncate ${isNaturePhase ? 'text-lg' : 'text-sm'}`}>{pose.pose_name}</h3>
                          {weight && (
                            <span className="text-[9px] font-black uppercase text-[#2D6A4F] bg-sage-50 px-2 py-0.5 rounded-full border border-sage-100">
                              {weight}
                            </span>
                          )}
                        </div>
                        <p className={`text-sage-500 uppercase tracking-[0.2em] font-black ${isNaturePhase ? 'text-[10px]' : 'text-[8px]'}`}>View {isNaturePhase ? 'Nature' : 'Pose'} Guidance</p>
                      </div>
                    </div>
                  );
                })}
            </div>
            <div className="bg-sage-50 p-4 rounded-xl border border-sage-100 w-full mt-4">
              <p className="text-xs text-sage-800 flex gap-2 items-start">
                <span className="text-xl">🧘</span>
                <span>
                  <strong>Educational guidance only. Not physical therapy.</strong> Consult your healthcare provider before starting new exercises, especially with injuries or physical limitations.
                </span>
              </p>
            </div>
          </div>
        )}
      </div>

      {selectedPose && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-hidden relative shadow-2xl flex flex-col md:flex-row shadow-sage-900/20">
            <button onClick={() => setSelectedPose(null)} className="absolute top-4 right-4 bg-black/50 text-white p-2 rounded-full z-20 hover:bg-black/70 transition-colors shadow-lg"><X size={20} /></button>
            
            {/* Left: Visual & Core Metadata */}
            <div className="w-full md:w-1/2 bg-sage-50 relative overflow-hidden flex flex-col">
              <div className="h-[40%] md:h-[45%] flex-shrink-0 relative bg-white">
                {selectedPose.image ? (
                  <PoseImage poseName={selectedPose.pose_name} imageResult={selectedPose.image} />
                ) : selectedPose.image_url ? (
                  <img src={selectedPose.image_url} alt={selectedPose.pose_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-sage-200 bg-sage-50">
                    <Camera size={64} />
                    <span className="text-xs font-bold uppercase mt-2">Visual unavailable</span>
                  </div>
                )}
                <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-md px-4 py-2 rounded-xl shadow-xl border border-white/50 z-20">
                  <p className="text-[10px] font-black uppercase text-sage-500 tracking-[0.2em] mb-0.5">Current Phase</p>
                  <p className="text-sage-900 font-serif font-bold text-base leading-none">{selectedPose.phase}</p>
                </div>
              </div>
              
              <div className="flex-1 p-8 bg-sage-900 text-white overflow-y-auto scrollbar-thin scrollbar-thumb-white/20">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${selectedPose.type === 'Exercise' ? 'bg-orange-500 text-white' : selectedPose.type === 'Pranayama' ? 'bg-blue-500 text-white' : 'bg-emerald-500 text-white'}`}>
                    {selectedPose.type}
                  </span>
                </div>
                <h2 className="text-2xl md:text-3xl font-serif font-bold mb-5 leading-tight">{selectedPose.pose_name}</h2>
                <div className="space-y-5">
                  <div>
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-sage-400 mb-2.5 flex items-center gap-2">
                       <Sparkles size={12} className="text-yellow-400" /> Therapeutic Benefit
                    </h3>
                    <p className="text-sage-100 text-sm italic leading-relaxed font-medium">"{selectedPose.benefit}"</p>
                  </div>
                  <div className="pt-5 border-t border-white/10">
                    <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-sage-400 mb-2.5 flex items-center gap-2">
                      <X size={12} className="text-red-400" /> Contraindications
                    </h3>
                    <p className="text-red-200 text-xs font-medium leading-relaxed opacity-90">{selectedPose.contraindications}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Personalization & Instructions */}
            <div className="w-full md:w-1/2 p-8 md:p-12 overflow-y-auto flex flex-col bg-white">
              {/* Instructions Section */}
              <div className="mb-10">
                <h3 className="font-black text-sage-900 mb-4 text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                  <Check className="text-emerald-500" size={16} strokeWidth={3} /> Standard Practice
                </h3>
                <div className="bg-sage-50/50 p-6 rounded-3xl border border-sage-100/50 shadow-inner">
                  <p className="text-sage-800 text-sm leading-8 whitespace-pre-wrap font-medium">
                    {selectedPose.instructions}
                  </p>
                </div>
              </div>

              {/* Dosha Personalization Tabs */}
              <div className="flex-1 flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-black text-indigo-900 text-xs uppercase tracking-[0.2em] flex items-center gap-2">
                    <Activity className="text-indigo-500" size={16} strokeWidth={3} /> Dosha Adaptation
                  </h3>
                  <div className="flex p-1 bg-gray-100 rounded-xl">
                    {(['vata', 'pitta', 'kapha'] as const).map((d) => (
                      <button
                        key={d}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDoshaTab(d);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all ${
                          activeDoshaTab === d
                            ? 'bg-white text-indigo-600 shadow-sm'
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                {selectedPose.doshaAdaptation ? (
                  <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                    {(() => {
                      const data = selectedPose.doshaAdaptation[activeDoshaTab];
                      if (!data) return <p className="text-xs text-gray-400 italic">No specific adaptation available for this Dosha.</p>;

                      return (
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white border-2 border-indigo-50 p-4 rounded-2xl shadow-sm hover:border-indigo-100 transition-colors">
                            <h4 className="text-[10px] font-black uppercase text-indigo-400 mb-1 tracking-wider">Duration & Breath</h4>
                            <p className="text-xs font-bold text-sage-900 mb-1">{data.duration}</p>
                            <p className="text-[11px] text-indigo-700 italic">{data.breathPattern}</p>
                          </div>
                          <div className="bg-white border-2 border-indigo-50 p-4 rounded-2xl shadow-sm hover:border-indigo-100 transition-colors">
                            <h4 className="text-[10px] font-black uppercase text-indigo-400 mb-1 tracking-wider">Intensity & Props</h4>
                            <p className="text-xs font-bold text-sage-900 mb-1">{data.intensity}</p>
                            <p className="text-[11px] text-indigo-700 italic">{data.props}</p>
                          </div>
                          <div className="col-span-2 bg-indigo-50/30 p-5 rounded-2xl border border-indigo-100/50">
                            <h4 className="text-[10px] font-black uppercase text-indigo-900 mb-2 tracking-widest flex items-center gap-2">
                              <Sparkles size={12} /> Mental Focus
                            </h4>
                            <p className="text-indigo-900 text-sm font-medium leading-relaxed italic">
                              "{data.mentalFocus}"
                            </p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ) : (
                  <div className="bg-gray-50 p-6 rounded-2xl text-center border border-dashed border-gray-200">
                    <p className="text-xs text-gray-500">Standard guidance applies to all constitutions for this movement.</p>
                  </div>
                )}
                
                <p className="text-[10px] text-sage-400 mt-auto pt-8 border-t border-gray-100 flex items-center gap-2 font-medium">
                  <Flower2 size={10} /> Personalization powered by Nature Nani's Ayurvedic logic engine.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showNatureModal && natureIntegrationDetail && (
        <NatureIntegrationDetail 
          data={natureIntegrationDetail} 
          onClose={() => setShowNatureModal(false)}
          activeDosha={userProfile?.vikriti_dosha || userProfile?.prakriti_dosha || 'vata'}
          imageUrl={routine.find(p => p.phase?.toLowerCase().includes('nature'))?.image?.url}
        />
      )}
    </div>
  );
};

export default YogaAid;
